import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Generate or get session ID for tracking
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('vergo_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('vergo_session_id', sessionId);
  }
  return sessionId;
};

export const useAffiliateTracking = () => {
  const { user } = useAuth();

  const trackClick = useCallback(async (
    botId: string,
    affiliateLinkId?: string,
    destinationUrl?: string
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('track-affiliate', {
        body: {
          botId,
          affiliateLinkId,
          sessionId: getSessionId(),
          referrer: document.referrer || window.location.href
        }
      });

      if (error) {
        console.error('Tracking error:', error);
      }

      // Return the redirect URL from server or use provided destination
      return data?.redirectUrl || destinationUrl;
    } catch (error) {
      console.error('Track click error:', error);
      return destinationUrl;
    }
  }, []);

  const trackAndRedirect = useCallback(async (
    botId: string,
    destinationUrl: string,
    affiliateLinkId?: string,
    openInNewTab = true
  ) => {
    const finalUrl = await trackClick(botId, affiliateLinkId, destinationUrl);
    
    if (finalUrl) {
      if (openInNewTab) {
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = finalUrl;
      }
    }
  }, [trackClick]);

  const getAffiliateLink = useCallback(async (botId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('affiliate_links')
        .select('id, affiliate_url, referral_code')
        .eq('bot_id', botId)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !data) return null;

      let url = data.affiliate_url;
      if (data.referral_code) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}ref=${data.referral_code}`;
      }
      
      return url;
    } catch (error) {
      console.error('Get affiliate link error:', error);
      return null;
    }
  }, []);

  return {
    trackClick,
    trackAndRedirect,
    getAffiliateLink,
    sessionId: getSessionId()
  };
};
